/**
 * air-reviews-lazy.js
 *
 * Performance fix for cocobear.store — defers AirReviews widget initialization
 * until each widget scrolls near the viewport, instead of letting the app
 * scan and process every `.AirReviews-Widget` instance on page load.
 *
 * Why this exists:
 * Collection and homepage grids render one AirReviews widget per product card.
 * On a page with 8–20+ cards, eager initialization of every widget at once is
 * a likely contributor to the "Forced reflow" and high Total Blocking Time
 * flagged in PageSpeed Insights, since the app's script has to read layout
 * for each star-rating element before painting it.
 *
 * How it works:
 * 1. Cards are rendered with `data-air-reviews-lazy` instead of being left
 *    for AirReviews to find immediately (see snippets/card-product.liquid).
 * 2. An IntersectionObserver watches for widgets approaching the viewport.
 * 3. Only when a widget is about to be visible do we remove the lazy marker
 *    and let AirReviews initialize that specific widget.
 * 4. A MutationObserver re-scans for newly added widgets after dynamic
 *    re-renders (filter changes, "load more", quick-add modals, etc.),
 *    since those elements won't exist yet on the initial page load.
 *
 * IMPORTANT — verify against the actual AirReviews app script:
 * This file assumes AirReviews scans the DOM for `.AirReviews-Widget`
 * elements automatically and has no documented manual init call, so the
 * fallback here is to simply reveal the widget (remove the lazy attribute)
 * and let the app's own MutationObserver/polling pick it up.
 *
 * If AirReviews exposes a real manual-init API (check the app's own script,
 * usually loaded via `content_for_header`, for something like
 * `window.AirReviews.init(el)` or `window.AirReviews.renderWidget(el)`),
 * swap the call in `revealWidget()` below to use that instead — a direct
 * API call is more reliable than waiting on the app's own DOM polling.
 */

(function () {
  'use strict';

  const LAZY_ATTR = 'data-air-reviews-lazy';
  const WIDGET_SELECTOR = '.AirReviews-Widget';

  // Bail out gracefully on very old browsers rather than throwing.
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('[' + LAZY_ATTR + ']').forEach(revealWidget);
    return;
  }

  const observer = new IntersectionObserver(handleIntersect, {
    root: null,
    rootMargin: '200px 0px', // start loading slightly before it's on-screen
    threshold: 0,
  });

  /**
   * Removes the lazy marker so the AirReviews app's own scanner picks up
   * the widget. Replace the body of this function with a direct API call
   * if AirReviews exposes one (see note above).
   */
  function revealWidget(el) {
    el.removeAttribute(LAZY_ATTR);

    // Preferred path if available — uncomment and adjust once confirmed:
    // if (window.AirReviews && typeof window.AirReviews.renderWidget === 'function') {
    //   window.AirReviews.renderWidget(el);
    //   return;
    // }

    // Fallback: nudge the app's own observer/polling by dispatching a
    // synthetic event some review apps listen for after DOM changes.
    el.dispatchEvent(new CustomEvent('air-reviews:widget-ready', { bubbles: true }));
  }

  function handleIntersect(entries, obs) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        revealWidget(entry.target);
        obs.unobserve(entry.target);
      }
    });
  }

  function observeAll(root) {
    root.querySelectorAll('[' + LAZY_ATTR + ']').forEach((el) => {
      observer.observe(el);
    });
  }

  // Initial pass over widgets present at page load.
  observeAll(document);

  // Re-scan when new product cards are added after the initial load —
  // collection filtering, "load more" pagination, and quick-add modals
  // all inject fresh card-product markup that won't exist yet above.
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return; // skip text/comment nodes
          if (node.matches && node.matches('[' + LAZY_ATTR + ']')) {
            observer.observe(node);
          }
          if (node.querySelectorAll) {
            observeAll(node);
          }
        });
      }
    }
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
